import type { DocumentType } from "@/features/documents/types";
/** Drivers API hook'lari. Query kalitlari ["drivers", ...] -- real-time "driver" hodisasi hammasini yangilaydi. */
import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { api, useIdempotencyKeys, type Paginated, type RequestOptions } from "@/lib/api";
import type {
  Activity,
  DriverDetail,
  DriverListItem,
  DriverWrite,
  DuplicateCheckResult,
  PipelineColumn,
  Reminder,
  StatusChangeInput,
  StatusCounts,
  StatusHistoryItem,
} from "./types";

export type DriverQuery = RequestOptions["query"];

export const driverKeys = {
  all: ["driver"] as const,
  list: (q: DriverQuery) => ["driver", "list", q] as const,
  counts: (q: DriverQuery) => ["driver", "counts", q] as const,
  detail: (id: number | string) => ["driver", "detail", String(id)] as const,
  activities: (id: number | string) => ["driver", "activities", String(id)] as const,
  history: (id: number | string) => ["driver", "history", String(id)] as const,
  reminders: (id: number | string) => ["driver", "reminders", String(id)] as const,
  pipeline: (q: DriverQuery) => ["driver", "pipeline", q] as const,
};

export function useDrivers(query: DriverQuery) {
  return useQuery({
    queryKey: driverKeys.list(query),
    queryFn: () => api.get<Paginated<DriverListItem>>("/api/drivers/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useDriverCounts(query: DriverQuery) {
  return useQuery({
    queryKey: driverKeys.counts(query),
    queryFn: () => api.get<StatusCounts>("/api/drivers/counts/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useDriver(id: number | string | undefined) {
  return useQuery({
    queryKey: driverKeys.detail(id ?? ""),
    queryFn: () => api.get<DriverDetail>(`/api/drivers/${id}/`),
    enabled: !!id,
  });
}

export function useDriverActivities(id: number | string | undefined, page = 1) {
  return useQuery({
    queryKey: [...driverKeys.activities(id ?? ""), page],
    queryFn: () => api.get<Paginated<Activity>>(`/api/drivers/${id}/activities/`, { query: { page, page_size: 50 } }),
    enabled: !!id,
  });
}

export function useDriverHistory(id: number | string | undefined) {
  return useQuery({
    queryKey: driverKeys.history(id ?? ""),
    queryFn: () => api.get<StatusHistoryItem[]>(`/api/drivers/${id}/history/`),
    enabled: !!id,
  });
}

export function useDriverReminders(id: number | string | undefined) {
  return useQuery({
    queryKey: driverKeys.reminders(id ?? ""),
    queryFn: () => api.get<Reminder[]>(`/api/drivers/${id}/reminders/`),
    enabled: !!id,
  });
}

export function usePipeline(query: DriverQuery) {
  return useQuery({
    queryKey: driverKeys.pipeline(query),
    queryFn: () => api.get<{ columns: PipelineColumn[]; total: number; fuzzy?: boolean }>("/api/drivers/pipeline/", { query }),
    placeholderData: keepPreviousData,
  });
}

export function useCreateDriver() {
  const qc = useQueryClient();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: (data: DriverWrite) => api.post<DriverDetail>("/api/drivers/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, data) => {
      keys.done(data);
      qc.invalidateQueries({ queryKey: driverKeys.all });
    },
  });
}

export function useUpdateDriver(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<DriverWrite> & { version: number }) => api.patch<DriverDetail>(`/api/drivers/${id}/`, data),
    onSuccess: (data) => {
      qc.setQueryData(driverKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: driverKeys.all });
    },
  });
}

export function useChangeStatus(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: StatusChangeInput) => api.post<DriverDetail>(`/api/drivers/${id}/status/`, data),
    onSuccess: (data) => {
      qc.setQueryData(driverKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: driverKeys.all });
    },
  });
}

export function useCheckDuplicate() {
  return useMutation({
    mutationFn: (data: { phone?: string; phone_secondary?: string; cdl_number?: string; email?: string; first_name?: string; last_name?: string; exclude?: number }) =>
      api.post<DuplicateCheckResult>("/api/drivers/check-duplicate/", data),
  });
}

export function useLogActivity(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { type: "call" | "note" | "sms" | "email"; body: string; duration_sec?: number | null; outcome?: string }) =>
      api.post<Activity>(`/api/drivers/${id}/activities/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: driverKeys.activities(id) });
      qc.invalidateQueries({ queryKey: driverKeys.detail(id) });
    },
  });
}

export function useCreateReminder(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { kind: string; due_at: string; note?: string; assigned_to_id?: number | null }) =>
      api.post<Reminder>(`/api/drivers/${id}/reminders/`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: driverKeys.reminders(id) });
      qc.invalidateQueries({ queryKey: driverKeys.detail(id) });
      qc.invalidateQueries({ queryKey: ["reminder"] });
    },
  });
}

export function useReminderAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action, snooze_until }: { id: number; action: "done" | "undo" | "snooze"; snooze_until?: string }) =>
      api.post<Reminder>(`/api/reminders/${id}/${action}/`, snooze_until ? { snooze_until } : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reminder"] });
      qc.invalidateQueries({ queryKey: driverKeys.all });
    },
  });
}

export function useTransferDriver(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { to_user: number | null; note?: string }) => api.post<DriverDetail>(`/api/drivers/${id}/transfer/`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: driverKeys.all }),
  });
}

export function useRequestTransfer() {
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) => api.post<{ approval_id: number; status: string }>(`/api/drivers/${id}/request-transfer/`, { note }),
  });
}

export function useMarkRetention(id: number | string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: 30 | 60 | 90) => api.post<DriverDetail>(`/api/drivers/${id}/retention/`, { days }),
    onSuccess: (data) => {
      qc.setQueryData(driverKeys.detail(id), data);
      qc.invalidateQueries({ queryKey: driverKeys.all });
    },
  });
}

/** Ochiq vakansiyalar (status o'zgartirish va topshirish oynalari uchun) -- hammasi, sahifalashsiz (backend jobs/options) */
export function useOpenJobs(enabled = true) {
  return useQuery({
    queryKey: ["job", "open-options"],
    queryFn: () => api.get<{ id: number; title: string; carrier_name: string; states: string[]; status: string }[]>("/api/jobs/options/"),
    enabled,
    staleTime: 60_000,
  });
}

/** Sozlamalar: rad etish sabablari va h.k. (Admin -> Settings) */
export interface AppSettings {
  /** Saqlashda majburiy driver maydonlari va yo'qotish sababi majburiymi (owner sozlaydi, 2026-10-18) */
  driver_form_required_fields?: string[];
  lost_reason_required?: boolean;
  rejection_reasons: string[];
  lost_reasons: string[];
  quit_reasons: string[];
  assignment_days: number;
  pipeline_stale_days: number;
  company_name: string;
  document_types?: DocumentType[];
  documents_block_submit?: boolean;
  documents_ocr_enabled?: boolean;
  documents_ocr_kinds?: string[];
  [key: string]: unknown;
  /** Carrier to'lovi muddati: ishga chiqqandan necha kun keyin */
  placement_fee_due_days?: number;
}

export function useAppSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: () => api.get<AppSettings>("/api/settings/"),
    staleTime: 5 * 60_000,
  });
}

/** Recruiterlar ro'yxati (filtrlar, o'tkazish) */
export interface UserOption {
  id: number;
  display_name: string;
  full_name: string;
  initials: string;
  role: string;
  branch: number | null;
  branch_name: string | null;
  is_blocked: boolean;
}

export function useUserOptions(query: RequestOptions["query"] = {}, enabled = true) {
  return useQuery({
    queryKey: ["user", "options", query],
    queryFn: () => api.get<Paginated<UserOption>>("/api/users/options/", { query }),  // sahifalashsiz: hammasi
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** Arxivlash (DELETE) -- backend faqat menejerga ruxsat beradi */
export function useArchiveDriver() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/api/drivers/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: driverKeys.all }),
  });
}
